import {
    Component,
    Input,
    ElementRef,
    OnInit,
    Output,
    EventEmitter,
    NgZone
} from '@angular/core';

/** Kakao 통합 로딩 (구 ssl.daumcdn.net URL은 404) */
const POSTCODE_SCRIPT_URL = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
const SCRIPT_SELECTOR = 'script[data-daum-postcode]';

@Component({
  selector: 'btn-daum-address',
  templateUrl: './daum-address.component.html',
  styleUrls: ['./daum-address.component.scss']
})
export class DaumAddressComponent implements OnInit {
  private static daumApiLoadPromise: Promise<void> | null = null;

  @Output() result = new EventEmitter<Object>();
  @Input() options: any;

  public styleClass: String;
  private el: ElementRef;
  private debug: false;

  constructor(el: ElementRef, private ngZone: NgZone) {
    this.el = el;
  }

  ngOnInit() {
    this.debug = this.options && this.options.debug ? this.options.debug : false;
    this.styleClass = this.options && this.options.class ? Array.isArray(this.options.class) ? this.options.class.join(' ') : this.options.class : '';
    this.removeStaleScript();
    this.ensureDaumApi().then(() => {
       this.print('Daum api has been loaded.');
    }).catch((e) => this.print(e));
  }

  private print(msg) {
    if (this.debug) {
      console.log(`[${Math.floor(new Date().getTime() / 1000)}]`, msg);
    }
  }

  private getPostcodeSdk(): any {
    const w = window as any;
    return w.kakao || w.daum || null;
  }

  private isDaumReady(): boolean {
    const sdk = this.getPostcodeSdk();
    return !!(sdk && typeof sdk.Postcode === 'function');
  }

  private removeStaleScript(): void {
    const existing = document.querySelector(SCRIPT_SELECTOR) as HTMLScriptElement | null;
    if (existing && existing.src !== POSTCODE_SCRIPT_URL) {
      existing.remove();
      DaumAddressComponent.daumApiLoadPromise = null;
    }
  }

  private resetScriptLoad(): void {
    const script = document.querySelector(SCRIPT_SELECTOR);
    if (script && script.parentNode) {
      script.parentNode.removeChild(script);
    }
    DaumAddressComponent.daumApiLoadPromise = null;
  }

  private ensureDaumApi(): Promise<void> {
    if (this.isDaumReady()) {
      return Promise.resolve();
    }
    if (DaumAddressComponent.daumApiLoadPromise) {
      return DaumAddressComponent.daumApiLoadPromise;
    }

    this.removeStaleScript();

    DaumAddressComponent.daumApiLoadPromise = new Promise((resolve, reject) => {
      const fail = (message: string) => {
        this.resetScriptLoad();
        reject(new Error(message));
      };

      const finish = () => {
        if (this.isDaumReady()) {
          resolve();
        } else {
          fail('우편번호 API를 불러왔지만 Postcode를 사용할 수 없습니다.');
        }
      };

      const existing = document.querySelector(SCRIPT_SELECTOR) as HTMLScriptElement | null;
      if (existing) {
        if (this.isDaumReady()) {
          resolve();
          return;
        }
        const onLoad = () => finish();
        const onError = () => fail('우편번호 API 스크립트 로드에 실패했습니다.');
        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', onError, { once: true });
        if (existing.getAttribute('data-daum-postcode-loaded') === 'true') {
          setTimeout(() => {
            if (this.isDaumReady()) {
              resolve();
            } else {
              fail('우편번호 API 스크립트를 다시 불러와 주세요.');
            }
          }, 0);
        }
        return;
      }

      const script = document.createElement('script');
      script.src = POSTCODE_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      script.setAttribute('data-daum-postcode', 'true');
      script.onload = () => {
        script.setAttribute('data-daum-postcode-loaded', 'true');
        finish();
      };
      script.onerror = () => fail('우편번호 API 스크립트 로드에 실패했습니다.');
      document.head.appendChild(script);
    });

    return DaumAddressComponent.daumApiLoadPromise;
  }

  private runWithPostcodeSdk(run: (sdk: any) => void): void {
    const sdk = this.getPostcodeSdk();
    if (!sdk) {
      console.error('[DaumAddress] postcode SDK is not available');
      return;
    }
    if (sdk.postcode && typeof sdk.postcode.load === 'function') {
      sdk.postcode.load(() => run(sdk));
    } else {
      run(sdk);
    }
  }

  private daumApiCallback(data) {
    this.print(data);
    let fullAddr = '', extraAddr = '', engAddr = '', zipCode = '';
    if (data.userSelectedType === 'R') {
      fullAddr = data.roadAddress;
      zipCode = data.zonecode;
      engAddr = data.roadAddressEnglish;
      if (data.bname !== '') {
        extraAddr += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName);
      }
      fullAddr += (extraAddr !== '' ? ' (' + extraAddr + ')' : '');
    } else {
      fullAddr = data.jibunAddress;
      zipCode = data.zonecode || data.postcode;
      engAddr = data.jibunAddressEnglish;
    }

    this.ngZone.run(() => {
      this.result.emit({
        zip: zipCode,
        addr: fullAddr,
        addrEng: engAddr
      });
    });
  }

  openDaumApi() {
    this.ensureDaumApi()
      .then(() => this.ngZone.run(() => this.openDaumPostcode()))
      .catch((e) => console.error('[DaumAddress]', e));
  }

  private openDaumPostcode() {
    const self = this;
    if (!this.options || (!this.options.type || this.options.type === 'popup')) {
      this.runWithPostcodeSdk((sdk) => {
        new sdk.Postcode({
            oncomplete: (data) => self.daumApiCallback(data)
        }).open();
      });
    } else {
      if (!this.options.target) {
        this.print('ERROR: Parent Component does not have a target element.');
        return false;
      }

      const $target = this.el.nativeElement.parentElement.querySelector(`#${this.options.target}`);
      this.print($target);
      switch (this.options.type) {
        case 'layer':
          const width = this.options.width || 300;
          const height = this.options.height || 460;
          const border = this.options.border || 5;
          this.runWithPostcodeSdk((sdk) => {
            new sdk.Postcode({
                oncomplete: (data) => self.daumApiCallback(data),
                onclose: () => $target.style.display = 'none',
                width: '100%',
                height: '100%'
            }).embed($target);
          });
          $target.style.display = 'block';
          $target.style.width = `${width}px`;
          $target.style.height = `${height}px`;
          $target.style.border = `${border}px solid`;
          $target.style.left = `${(((window.innerWidth || document.documentElement.clientWidth) - width) / 2 - border)}px`;
          $target.style.top = `${(((window.innerHeight || document.documentElement.clientHeight) - height) / 2 - border)}px`;
          try {
            $target.querySelector('#btnCloseLayer').onclick = () => {
              $target.style.display = 'none';
            };
          } catch (e) {
            this.print(`ERROR: ${e.message}`);
          }
        break;
        case 'inline':
          const currentScroll = Math.max(document.body.scrollTop, document.documentElement.scrollTop);
          this.runWithPostcodeSdk((sdk) => {
            new sdk.Postcode({
                oncomplete: (data) => {
                  self.daumApiCallback(data);
                  document.body.scrollTop = currentScroll;
                },
                onclose: () => $target.style.display = 'none',
                onresize: (size) => $target.style.height = size.height + 'px',
                width: '100%',
                height: '100%'
            }).embed($target);
          });
          $target.style.display = 'block';
          try {
            $target.querySelector('#btnFoldWrap').onclick = () => {
              $target.style.display = 'none';
            };
          } catch (e) {
            this.print(`ERROR: ${e.message}`);
          }
        break;
      }
    }
  }

}
